/**
 * API Endpoint Constants
 * Centralized location for all API endpoints
 */

export const API_ENDPOINTS = {
	AUTH: {
		LOGIN: '/auth/login',
		REFRESH: '/auth/refresh',
		IMPERSONATE: '/auth/impersonate',
		USER_ACCOUNTS: '/auth/user-accounts',
		SWITCH_ACCOUNT: '/auth/switch-account',
		SSO: '/auth/sso',
		FORGOT_PASSWORD: '/auth/forgot-password',
		SET_PASSWORD: '/auth/set-password'
	},

	SENTINEL: {
		SETTINGS: '/sentinel/settings',
		ROUTING: '/sentinel/settings/routing',
		USER_BRANDS: '/sentinel/user-brands',
		USER_BRANDS_OF: (userId: number) => `/sentinel/users/${userId}/brands`,
		BRAND_CAPS: '/sentinel/brand-caps',
		BRAND_OWN_CAPS: '/sentinel/brand-caps/own-caps',
		UNASSIGNED_COUNTS: '/sentinel/tickets/unassigned-counts',
		MY_BRANDS: '/sentinel/brands/mine',
		PRESENCE: '/sentinel/presence',
		AVAILABILITY: '/sentinel/availability',
		BRAND_HEALTH: {
			OVERVIEW: '/sentinel/brand-health/overview',
			FILTERS: '/sentinel/brand-health/filters'
		},
		SENTINEL_ANALYTICS: {
			OVERVIEW: '/sentinel/analytics/overview'
		},
		TICKETS: '/sentinel/tickets',
		TICKET: (ticketId: number) => `/sentinel/tickets/${ticketId}`,
		CONVERSATION_CONTEXT: (conversationId: number) => `/sentinel/conversations/${conversationId}/context`,
		ATTACHMENT: (ticketId: number, messageId: number, attachmentIndex: number) =>
			`/sentinel/tickets/${ticketId}/messages/${messageId}/attachments/${attachmentIndex}`,
		ATTACHMENT_UPLOAD: (ticketId: number) => `/sentinel/tickets/${ticketId}/attachments/upload-url`,
		PROVIDER_AGENTS: (ticketId: number) => `/sentinel/tickets/${ticketId}/provider-agents`,
		TICKET_TAGS: (ticketId: number) => `/sentinel/tickets/${ticketId}/tags`,
		CUSTOMER_TICKETS: (ticketId: number) => `/sentinel/tickets/${ticketId}/customer-tickets`,
		HELPDESK_URL: (ticketId: number) => `/sentinel/tickets/${ticketId}/helpdesk-url`,
		SIMILAR_TICKET_THREAD: (ticketId: number, conversationId: string) =>
			`/sentinel/tickets/${ticketId}/similar-tickets/${encodeURIComponent(conversationId)}`,
		ACCEPT_BLUEPRINT: (ticketId: number) => `/sentinel/tickets/${ticketId}/blueprint/accept`,
		BLUEPRINT_FEEDBACK: (ticketId: number) => `/sentinel/tickets/${ticketId}/feedbacks`,
		FEEDBACKS: '/sentinel/feedbacks',
		BLUEPRINT_RETRY: (ticketId: number) => `/sentinel/tickets/${ticketId}/blueprint/retry`,
		REFINE_RESPONSE: (ticketId: number) => `/sentinel/tickets/${ticketId}/response/refine`,
		HITL_DECISION: (ticketId: number) => `/sentinel/tickets/${ticketId}/hitl/decision`,
		REJECT: (ticketId: number) => `/sentinel/tickets/${ticketId}/reject`,
		SEND_RESPONSE: (ticketId: number) => `/sentinel/tickets/${ticketId}/response/send`,
		ADD_NOTE: (ticketId: number) => `/sentinel/tickets/${ticketId}/notes`,
		ASSIGN_TO_ME: (ticketId: number) => `/sentinel/tickets/${ticketId}/assign-to-me`,
		UNASSIGN_ME: (ticketId: number) => `/sentinel/tickets/${ticketId}/unassign`,
		POST_PROCESSING_EXECUTE: (ticketId: number) => `/sentinel/tickets/${ticketId}/post-processing/execute`,
		FOLLOWUPS: {
			LIST: '/sentinel/followups',
			DETAIL: (id: string) => `/sentinel/followups/${encodeURIComponent(id)}`,
			CANCEL: (id: string) => `/sentinel/followups/${encodeURIComponent(id)}/cancel`,
			SEND: (id: string) => `/sentinel/followups/${encodeURIComponent(id)}/send`,
			OPT_OUTS: '/sentinel/followups/opt-outs',
			OPT_OUT: (customerKey: string) => `/sentinel/followups/opt-outs/${encodeURIComponent(customerKey)}`,
			REPORT: '/sentinel/followups/report',
			/** The open ticket's own follow-up: GET it, POST to set or replace it. */
			TICKET: (ticketId: number) => `/sentinel/tickets/${ticketId}/followup`
		}
	},

	SOURCES: {
		BASE: '/sources',
		STATS: '/sources/stats',
		MY_SOURCES: '/sources/my-sources',
		BY_ID: (id: string) => `/sources/${id}`,
		CONTENT: (id: string) => `/sources/${id}/content`,
		EXPORT_PREVIEW: (id: string) => `/sources/${id}/export-preview`,
		STATUS: (id: string) => `/sources/${id}/status`,
		RESYNC: (id: string) => `/sources/${id}/resync`,
		CATEGORIZATION: (id: string) => `/sources/${id}/categorization`,
		AUTO_CATEGORIZE: '/sources/auto-categorize'
	},

	KNOWLEDGE_BASES: {
		BASE: '/knowledge-bases',
		STATS: '/knowledge-bases/stats',
		TAGS: '/knowledge-bases/tags',
		BATCH: '/knowledge-bases/by-ids',
		BY_ID: (id: string) => `/knowledge-bases/${id}`,
		STATUS: (id: string) => `/knowledge-bases/${id}/status`,
		SOURCES: (id: string) => `/knowledge-bases/${id}/sources`,
		INSTRUCTIONS: (id: string) => `/knowledge-bases/${id}/instructions`,
		CHANNELS: (id: string) => `/knowledge-bases/${id}/channels`
	},

	BUSINESS_UNITS: {
		LIST: '/business-units',
		ALL: '/business-units/all'
	},

	PROMOTED_BLUEPRINTS: {
		LIST: '/promoted-blueprints',
		BY_ID: (id: string) => `/promoted-blueprints/${id}`,
		ENABLED: (id: string) => `/promoted-blueprints/${id}/enabled`,
		AUTO_SEND: (id: string) => `/promoted-blueprints/${id}/auto-send`,
		TEMPLATE_ENABLED: (id: string, index: number) => `/promoted-blueprints/${id}/templates/${index}/enabled`,
		/** Each template with the scenario type it answers and that type's back-testing verdict. */
		TEMPLATE_READINESS: (id: string) => `/promoted-blueprints/${id}/template-readiness`,
		TEMPLATE_TYPE_LINK: (id: string, index: number) => `/promoted-blueprints/${id}/templates/${index}/type-link`,
		APPLY_READINESS: (id: string) => `/promoted-blueprints/${id}/apply-readiness`,
		/** Which tool steps of the blueprint run without supervisor approval. */
		AUTOMATION: (id: string) => `/promoted-blueprints/${id}/automation`,
		KNOWLEDGE_INTENT_ENABLED: (intent: string) =>
			`/promoted-blueprints/knowledge-intents/${encodeURIComponent(intent)}/enabled`
	},

	INSTRUCTIONS: {
		BASE: '/instructions',
		STATS: '/instructions/stats',
		TAGS: '/instructions/tags',
		BY_ID: (id: string) => `/instructions/${id}`,
		BY_TYPE: (type: string) => `/instructions/type/${type}`,
		STATUS: (id: string) => `/instructions/${id}/status`,
		KNOWLEDGE_BASES: (id: string) => `/instructions/${id}/knowledge-bases`
	},

	CHANNELS: {
		BASE: '/channels',
		STATS: '/channels/stats',
		TAGS: '/channels/tags',
		DEFAULT: '/channels/default',
		CUSTOM: '/channels/custom',
		BEHAVIOUR: '/channels/behaviour',
		BY_ID: (id: string) => `/channels/${id}`
	},

	STORAGE: {
		UPLOAD_URL: '/storage/upload-url',
		DOWNLOAD_URL: '/storage/download-url',
		DELETE_URL: '/storage/delete-url',
		DELETE: (key: string) => `/storage/${key}`
	},

	INTEGRATIONS: {
		ACCOUNT: '/integrations/account',
		DISCONNECT: (id: string) => `/integrations/${id}/disconnect`
	},

	GORGIAS: {
		DISCONNECT: '/gorgias/disconnect'
	},

	HELPSCOUT: {
		AUTHORIZE: '/helpscout/oauth/authorize',
		CONNECTION_STATUS: '/helpscout/connection/status',
		DISCONNECT: '/helpscout/disconnect'
	},

	GOOGLE: {
		OAUTH_INSTALL: '/integrations/google/oauth/install'
	},

	SMARTRR: {
		CONNECT: '/integrations/smartrr/connect'
	},
	SKIO: {
		CONNECT: '/integrations/skio/connect'
	},

	INFOPLUS: {
		CONNECT: '/integrations/infoplus/connect'
	},

	CLICKPOST: {
		CONNECT: '/integrations/clickpost/connect'
	},

	ZENDESK: {
		INSTALL: '/zendesk/oauth/install'
	},

	RICHPANEL: {
		CONNECT: '/integrations/richpanel/connect',
		LIST_USERS: '/integrations/richpanel/users',
		ENSURE_TAGS: '/integrations/richpanel/ensure-tags'
	},

	FRESHDESK: {
		VERIFY: '/integrations/freshdesk/verify',
		CONNECT: '/integrations/freshdesk/connect',
		REPLACE_API_KEY: '/integrations/freshdesk/api-key',
		STAGE_WEBHOOK_TOKEN: '/integrations/freshdesk/webhook-token/stage',
		ACTIVATE_WEBHOOK_TOKEN: '/integrations/freshdesk/webhook-token/activate',
		CANCEL_WEBHOOK_TOKEN: '/integrations/freshdesk/webhook-token/cancel',
		DISCONNECT: '/integrations/freshdesk/disconnect'
	},

	INTERCOM: {
		INSTALL: '/integrations/intercom/oauth/install',
		CONNECT: '/integrations/intercom/connect',
		OWN_APP_SETUP: '/integrations/intercom/own-app/setup',
		TEAMMATES: '/integrations/intercom/teammates',
		TEAMMATE: '/integrations/intercom/teammate'
	},

	JUDGEME: {
		CONNECT: '/integrations/judgeme/connect'
	},

	AFTERSHIP: {
		CONNECT: '/integrations/aftership/connect'
	},
	WONDERMENT: {
		CONNECT: '/integrations/wonderment/connect'
	},
	CHECKOUTCHAMP: {
		CONNECT: '/integrations/checkoutchamp/connect'
	},
	SHIPBOB: {
		CHANNELS: '/integrations/shipbob/channels',
		CONNECT: '/integrations/shipbob/connect'
	},

	RECHARGE: {
		CONNECT: '/integrations/recharge/connect'
	},

	SHIPSTATION: {
		CONNECT: '/integrations/shipstation/connect'
	},

	LOOPRETURNS: {
		CONNECT: '/integrations/loopreturns/connect'
	},

	SHOPIFY: {
		INSTALL: '/shopify/install',
		SCOPES: '/shopify/scopes',
		PRIMARY: '/shopify/primary'
	},

	YOTPO: {
		CONNECT: '/integrations/yotpo/connect'
	},

	SLACK: {
		CONNECT: '/integrations/slack/connect'
	},

	KLAVIYO: {
		CONNECT: '/integrations/klaviyo/connect'
	},

	USPS: {
		CONNECT: '/integrations/usps/connect'
	},

	ROYALMAIL: {
		CONNECT: '/integrations/royalmail/connect'
	},

	REVIEWSIO: {
		CONNECT: '/integrations/reviewsio/connect'
	},

	TRUSTPILOT: {
		CONNECT: '/integrations/trustpilot/connect'
	},

	WAREHANCE: {
		CONNECT: '/integrations/warehance/connect'
	},

	KUSTOMER: {
		CONNECT: '/integrations/kustomer/connect'
	},

	KIM_HELPDESK: {
		CONNECT: '/integrations/helpdesk/connect'
	},

	GLADLY: {
		CONNECT: '/integrations/gladly/connect',
		LIST_USERS: '/integrations/gladly/users'
	},

	SOCIAL_AGENT: {
		RULES: '/social-agent/rules',
		RULE: (id: string) => `/social-agent/rules/${id}`,
		// Separate from RULE: enable/disable is the only write a Kim-authored rule accepts besides
		// channel scope, so it must not go through the general update route.
		RULE_STATUS: (id: string) => `/social-agent/rules/${id}/status`,
		// Account-level, not rule-level: one object per account. Currently the reply delay.
		SETTINGS: '/social-agent/settings',
		// The append-only action log. Read-only by design — there is no update or delete route to
		// offer. The export takes the SAME filter contract as the list, only rendered as CSV.
		LOGS: '/social-agent/logs',
		LOGS_EXPORT: '/social-agent/logs/export'
	},

	SALES_NUDGES: {
		BASE: '/nudges',
		FIELDS: '/nudges/fields',
		BY_ID: (id: string) => `/nudges/${encodeURIComponent(id)}`,
		STATUS: (id: string) => `/nudges/${encodeURIComponent(id)}/status`
	},

	META: {
		INSTALL: '/integrations/meta/oauth/install',
		CHANNELS: '/integrations/meta/channels',
		// One Meta row holds several Pages / Instagram accounts, so removal is per credential —
		// dropping one Page must not disconnect the rest.
		CREDENTIAL: (credentialId: string) => `/integrations/meta/credentials/${encodeURIComponent(credentialId)}`
	},

	MESSAGES: {
		CONVERSATIONS: '/messages/conversations',
		CONVERSATION_BY_ID: (id: number) => `/messages/conversations/${id}`
	},

	AGENTS: {
		BASE: '/agents'
	},

	GUARDRAILS: {
		BASE: '/guardrails',
		STATS: '/guardrails/stats',
		SYSTEM: '/guardrails/system',
		CUSTOM: '/guardrails/custom',
		ACTIVE: '/guardrails/active',
		BY_ID: (id: string) => `/guardrails/${id}`
	},

	GUARDRAILS_FALLBACK: {
		BASE: '/guardrails-fallback',
		STATS: '/guardrails-fallback/stats',
		BY_ID: (id: string) => `/guardrails-fallback/${id}`
	},

	INBOUND_FILTERS: {
		BASE: '/inbound-filters',
		TICKET_FIELDS: '/inbound-filters/ticket-fields'
	},

	ACCOUNTS: {
		BASE: '/accounts',
		FLAG: (id: string) => `/accounts/${id}/flag`,
		USERS: (id: string) => `/accounts/${id}/users`,
		FLAGS: '/accounts/flags',
		KILL_SWITCH: '/accounts/kill-switch',
		SHADOW_MODE: '/accounts/shadow-mode',
		AI_ENABLED: '/accounts/ai-enabled',
		INTENT_FILTERING_ENABLED: '/accounts/intent-filtering-enabled'
	},

	ACCOUNT_INTENT_CONFIGS: {
		BASE: '/account-intent-configs',
		ENABLED: '/account-intent-configs/enabled',
		CHECKS: '/account-intent-configs/checks'
	},

	ACCOUNT_INTENT_FILTER_CONFIGS: {
		BASE: '/account-intent-filter-configs',
		ENABLED: '/account-intent-filter-configs/enabled',
		BYPASS_TAGGING: '/account-intent-filter-configs/bypass-tagging'
	},

	TOOL_CLIENTS: {
		BASE: '/tool-clients',
		BY_ID: (id: string) => `/tool-clients/${id}`,
		TIER: (id: string) => `/tool-clients/${id}/tier`,
		ROTATE: (id: string) => `/tool-clients/${id}/rotate`
	},

	ESCALATION_LOGS: {
		LIST: '/escalation-logs',
		SUMMARY: '/escalation-logs/summary'
	},
	SETTINGS_LOGS: {
		LIST: '/settings-logs'
	},
	AUTOMATION_REPLIES: {
		LIST: '/automation-replies',
		INTENTS: '/automation-replies/intents',
		BY_ID: (id: string) => `/automation-replies/${encodeURIComponent(id)}`,
		THREAD: (id: string) => `/automation-replies/${encodeURIComponent(id)}/thread`,
		REVIEW: (id: string) => `/automation-replies/${encodeURIComponent(id)}/review`,
		RUN_CHECKS: '/automation-replies/checks/run',
		CHECKS: (id: string) => `/automation-replies/${encodeURIComponent(id)}/checks`
	},

	ACCOUNT_USERS: {
		BASE: '/account-users',
		BULK: '/account-users/bulk',
		ALIASES: (id: number) => `/account-users/${id}/aliases`,
		ALIAS_BY_ID: (id: number, aliasId: number) => `/account-users/${id}/aliases/${aliasId}`
	},

	BRAND: {
		SETTINGS: '/brand/settings',
		AUTOMATION_THRESHOLD: '/brand/automation-threshold',
		AUTOMATION_GUARDS: '/brand/automation-guards',
		BLOCKED_INTENTS: '/brand/blocked-intents',
		SPAM_TICKET_FIELDS: '/brand/spam-ticket-fields',
		AI_SETTINGS: '/brand/ai-settings',
		STORE_TIMEZONE: '/brand/ai-settings/store-timezone',
		ESCALATION_POLICY: '/brand/escalation-policy',
		HELPDESK_AGENTS: '/brand/helpdesk-agents',
		TICKET_PREPROCESSING: '/brand/ticket-preprocessing'
	},

	WORKFLOWS: {
		BASE: '/workflows',
		DEFAULT: '/workflows/default',
		BY_ID: (id: string) => `/workflows/${id}`,
		TOGGLE: (id: string) => `/workflows/toggle/${id}`,
		BY_BRAND: (id: number | string) => `/workflows/brand/${id}`,
		CHANNELS: (id: string) => `/workflows/${id}/channels`,
		SEG: (id: string) => `/workflows/${id}/seg`,
		SEG_NODE: (id: string) => `/workflows/${id}/seg-node`,
		RESYNC: (id: string) => `/workflows/${id}/resync`,
		/** The workflow's text format — what its linked SOP holds. */
		TEXT: (id: string) => `/workflows/${id}/text`,
		/** Compile a protocol through the question-asking pipeline. */
		COMPILE_GUIDED: (id: string) => `/workflows/${id}/compile/guided`,
		/** Propose an AI edit to the protocol's document; saves nothing. */
		AI_EDIT: (id: string) => `/workflows/${id}/ai-edit`,
		/** Read-only AI review of the protocol's document: an audit, or improvement ideas. */
		AI_REVIEW: (id: string) => `/workflows/${id}/ai-review`,
		CATEGORY_TREE: '/workflows/category-tree',
		AUTO_CATEGORIZE: '/workflows/auto-categorize',
		GENERATE_PROTOCOL: '/workflows/protocols/generate'
	},
	TOOL_DEFINITIONS: {
		BASE: '/tool-definitions',
		RUN: '/tool-definitions/run'
	},

	TOOL_PRECONDITIONS: {
		BASE: '/tool-preconditions',
		BY_ID: (id: string) => `/tool-preconditions/${id}`,
		COMBINATOR: '/tool-preconditions/combinator'
	},

	TOOL_EXECUTION_LOGS: {
		BASE: '/tool-execution-logs'
	},

	CUSTOM_TOOLS: {
		BASE: '/custom-tools',
		BY_ID: (id: string) => `/custom-tools/${id}`,
		SLUG_CHECK: '/custom-tools/slug-check',
		SUGGEST: '/custom-tools/suggest',
		TEST: '/custom-tools/test'
	},

	/** Knowledge promotion: Q&A items from articles or imports, checked, promoted as drafts, auto-send once earned. */
	KNOWLEDGE_PROMOTION: {
		ITEMS: '/knowledge-promotion/items',
		ITEM: (id: string) => `/knowledge-promotion/items/${id}`,
		PENDING: (id: string) => `/knowledge-promotion/items/${id}/pending`,
		CHECK: (id: string) => `/knowledge-promotion/items/${id}/check`,
		PROMOTE: (id: string) => `/knowledge-promotion/items/${id}/promote`,
		STOP: (id: string) => `/knowledge-promotion/items/${id}/stop`,
		AUTO_SEND: (id: string) => `/knowledge-promotion/items/${id}/auto-send`,
		TRY: '/knowledge-promotion/try',
		ARTICLES: '/knowledge-promotion/articles',
		GENERATE: '/knowledge-promotion/generate',
		IMPORT_PREVIEW: '/knowledge-promotion/import/preview',
		IMPORT: '/knowledge-promotion/import',
		JOBS: '/knowledge-promotion/jobs',
		JOB: (id: string) => `/knowledge-promotion/jobs/${id}`
	},

	/** Protocol-level promotion: generate templates for back-tested scenario types and promote them. */
	PROMOTION: {
		PROTOCOLS: '/promotion/protocols',
		PROTOCOL: (sopId: string) => `/promotion/protocols/${sopId}`,
		GENERATE: (sopId: string) => `/promotion/protocols/${sopId}/generate`,
		JOB: (sopId: string) => `/promotion/protocols/${sopId}/job`,
		APPLY: (sopId: string) => `/promotion/protocols/${sopId}/apply`,
		PLAN: (sopId: string) => `/promotion/protocols/${sopId}/promotion`,
		PREVIEW: (sopId: string) => `/promotion/protocols/${sopId}/preview`,
		PROMOTE: (sopId: string) => `/promotion/protocols/${sopId}/promote`,
		STOP: (sopId: string) => `/promotion/protocols/${sopId}/stop`,
		HISTORY: (sopId: string) => `/promotion/protocols/${sopId}/promotions`,
		SETTINGS: '/promotion/settings'
	},

	AI_FUNCTIONS: {
		BASE: '/ai-functions',
		STREAM: (name: string) => `/ai-functions/${name}/stream`,
		RUN: (name: string) => `/ai-functions/${name}`,
		BACK_TESTING: (sopId: string) => `/ai-functions/back-testing/${sopId}`,
		/** One scenario by its own id; no SOP needed, the scenario says which it is. */
		SCENARIO: (scenarioId: string) => `/ai-functions/back-testing/scenarios/${scenarioId}`,
		BUCKETING: (sopId: string) => `/ai-functions/back-testing/${sopId}/bucket`,
		TYPE_SEARCH: (sopId: string) => `/ai-functions/back-testing/${sopId}/type-search`,
		/** Each type's promotion verdict, derived from its latest finished bulk run. */
		READINESS: (sopId: string) => `/ai-functions/back-testing/${sopId}/readiness`,
		/** A type's latest bulk run, and where a new one starts. */
		BULK_RUN: (sopId: string, typeId: string) => `/ai-functions/back-testing/${sopId}/types/${typeId}/bulk-run`,
		/** Every bulk run of a type, newest first, and one of them by id. */
		BULK_RUNS: (sopId: string, typeId: string) => `/ai-functions/back-testing/${sopId}/types/${typeId}/bulk-runs`,
		BULK_RUN_BY_ID: (sopId: string, typeId: string, runId: string) =>
			`/ai-functions/back-testing/${sopId}/types/${typeId}/bulk-runs/${runId}`,
		/** A person's Correct/Incorrect mark on one settled reply of a finished bulk run. */
		BULK_RUN_ITEM_REVIEW: (sopId: string, typeId: string, runId: string, scenarioId: string) =>
			`/ai-functions/back-testing/${sopId}/types/${typeId}/bulk-runs/${runId}/items/${scenarioId}/review`,
		SCENARIO_RUNS: (sopId: string, scenarioId: string) =>
			`/ai-functions/back-testing/${sopId}/scenarios/${scenarioId}/runs`,
		/** On-demand review of one simulator run: judge it, then investigate why. */
		PLAYGROUND_JUDGE: '/ai-functions/playground/judge',
		PLAYGROUND_INVESTIGATE: '/ai-functions/playground/investigate',
		/** Kept simulator runs: the account's list, and one run whole to reopen it. */
		SIMULATOR_RUNS: '/ai-functions/simulator/runs',
		SIMULATOR_RUN: (requestId: string) => `/ai-functions/simulator/runs/${encodeURIComponent(requestId)}`
	},

	SIMULATION_DATASETS: {
		BASE: '/simulation-datasets',
		BY_ID: (id: string) => `/simulation-datasets/${id}`,
		/** Where more of the type's scenarios join the dataset as cases. */
		CASES: (id: string) => `/simulation-datasets/${id}/cases`,
		CASE: (id: string, caseId: string) => `/simulation-datasets/${id}/cases/${caseId}`,
		CAPTURES: (id: string) => `/simulation-datasets/${id}/captures`,
		SOURCE_NAMES: (id: string) => `/simulation-datasets/${id}/source-names`,
		/** Where a run starts, and every run of the dataset, newest first. */
		RUNS: (id: string) => `/simulation-datasets/${id}/runs`,
		RUN_BY_ID: (id: string, runId: string) => `/simulation-datasets/${id}/runs/${runId}`,
		/** The latest run. */
		RUN: (id: string) => `/simulation-datasets/${id}/run`,
		CASE_EVALUATION: (id: string, caseId: string) => `/simulation-datasets/${id}/cases/${caseId}/evaluation`,
		EVALUATIONS: (id: string) => `/simulation-datasets/${id}/evaluations`
	},

	SEGS: {
		BASE: '/segs',
		COMBINED: '/segs/combined',
		BY_ID: (id: string) => `/segs/${id}`,
		TOGGLE_STATUS: (id: string) => `/segs/${id}/toggle-status`
	},

	TICKETS: {
		THREAD: (externalTicketId: string) => `/tickets/${externalTicketId}/thread`
	},

	SOPS: {
		BASE: '/sops',
		BY_ID: (id: string) => `/sops/${id}`,
		TICKETS: (id: string) => `/sops/${id}/tickets`,
		SUBCATEGORY_TICKETS: (id: string) => `/sops/${id}/subcategory-tickets`,
		CONTENT: (id: string) => `/sops/${id}/content`,
		APPROVAL: (id: string) => `/sops/${id}/approval`,
		RESYNC: (id: string) => `/sops/${id}/resync`,
		STATUS: (id: string) => `/sops/${id}/status`,
		CATEGORIZATION: (id: string) => `/sops/${id}/categorization`,
		RENAME: (id: string) => `/sops/${id}/rename`,
		MERGE: '/sops/merge',
		MERGE_BATCH: '/sops/merge-batch',
		CATEGORIZATION_TREE: '/sops/categorization-tree',
		/** (Re)build the protocol for an approved SOP. */
		BUILD_PROTOCOL: (id: string) => `/sops/${id}/protocol`,
		/** Add a SOP no workflow owns to an existing protocol (then it is retired). */
		FOLD: (id: string) => `/sops/${id}/fold`,
		/** Turn a SOP no workflow owns into a knowledge-base article (then it is retired). */
		ARTICLE: (id: string) => `/sops/${id}/article`
	},

	PERSONA: {
		BASE: '/persona',
		BY_ID: (id: string) => `/persona/${id}`,
		CONTENT: (id: string, version?: string) => `/persona/${id}/content${version ? `?version=${version}` : ''}`,
		VERSIONS: (id: string) => `/persona/${id}/versions`,
		CHANGES: (id: string) => `/persona/${id}/changes`,
		REFINE: (id: string) => `/persona/${id}/refine`,
		VALIDATE_EXAMPLES: (id: string) => `/persona/${id}/validate-examples`,
		RESTORE_VALIDATED: (id: string) => `/persona/${id}/restore-validated`,
		JOB_STATUS: (jobId: string) => `/persona/jobs/${jobId}`
	},

	PULSE: {
		SUBCATEGORY_COUNTS: '/pulse/subcategory-counts',
		GENERATE_SOP: (sopCatId: string) => `/sops/generate/${sopCatId}`,
		JOB_STATUS: (jobId: string) => `/sops/jobs/${jobId}`
	},

	CS_CATEGORIES: {
		BASE: '/cs-categories',
		OPTIONS: '/cs-categories/options',
		BY_ID: (id: string) => `/cs-categories/${id}`,
		ENABLED: (id: string) => `/cs-categories/${id}/enabled`,
		// Super-admin only: the shared templates every account/BU copy is seeded from.
		TEMPLATES: {
			BASE: '/cs-categories/templates',
			OPTIONS: '/cs-categories/templates/options',
			BY_ID: (id: string) => `/cs-categories/templates/${id}`,
			ENABLED: (id: string) => `/cs-categories/templates/${id}/enabled`,
			PROPAGATE_PREVIEW: (id: string) => `/cs-categories/templates/${id}/propagate/preview`,
			PROPAGATE: (id: string) => `/cs-categories/templates/${id}/propagate`,
			BUSINESS_UNITS: '/cs-categories/templates/business-units'
		}
	},

	REPORTS: {
		SHADOW_EMAIL: '/reports/shadow/email'
	},

	// v2 analytics is fronted by the kim-ai-backend `/api/analytics/*` proxy
	// (one frontend → one backend; kim-analytics is a downstream service, not exposed).
	ANALYTICS_V2: {
		METRICS: '/analytics/metrics',
		METRICS_SERIES: '/analytics/metrics/series',
		CATEGORIES: '/analytics/categories',
		FUNNEL: '/analytics/funnel',
		HEALTH: '/analytics/health/check'
	},

	EXTENSION_VERSION: {
		VERSIONS: '/extension/admin/versions'
	},

	ME: {
		PERMISSIONS: '/me/permissions'
	},

	ROLE_PERMISSIONS: {
		BASE: '/admin/role-permissions',
		BY_ROLE: (role: string) => `/admin/role-permissions/${role}`
	},

	FEEDBACKS: {
		ADMIN_LIST: '/feedback/admin',
		MARK_ADDRESSED: (id: string) => `/feedback/admin/${id}/address`,
		UNMARK_ADDRESSED: (id: string) => `/feedback/admin/${id}/unaddress`
	},

	WORKFLOW_TEMPLATES: {
		BASE: '/workflow-templates',
		BY_ID: (id: string) => `/workflow-templates/${id}`,
		USE: (id: string) => `/workflow-templates/${id}/use`
	},

	RESPONSE_STRATEGIES: {
		BASE: '/response-strategies',
		BY_ID: (id: string) => `/response-strategies/${id}`,
		STATUS: (id: string) => `/response-strategies/${id}/status`,
		GENERATE: '/response-strategies/generate',
		FROM_TEMPLATE: (templateId: string) => `/response-strategies/from-template/${templateId}`
	},

	RESPONSE_STRATEGY_TEMPLATES: {
		BASE: '/response-strategy-templates',
		BY_ID: (id: string) => `/response-strategy-templates/${id}`
	},

	PRODUCTS: {
		RECOMMENDATIONS: '/products/recommendations',
		RECOMMENDATION_OPTIONS: '/products/recommendations/options',
		BASE: '/products',
		BY_ID: (id: string) => `/products/${id}`,
		CUSTOM_INFO: (id: string) => `/products/${id}/custom-info`,
		CUSTOM_INFO_ENTRY: (productId: string, entryId: string) =>
			`/products/${productId}/custom-info/${entryId}`,
		CUSTOM_INFO_AUDIT: (id: string) => `/products/${id}/custom-info/audit`,
		SYNC: '/products/sync',
		SYNC_STATUS: (jobId: string) => `/products/sync/${jobId}`,
		SYNC_CONFIG: '/products/sync-config'
	},

	// AI Operating Dashboard gap board — reads the Mongo `gaps` store via kim-ai-backend.
	GAPS: {
		LIST: '/gaps',
		INTENTS: '/gaps/intents',
		BY_ID: (id: string) => `/gaps/${id}`,
		THREAD: (id: string) => `/gaps/${id}/thread`,
		ACTIONS: (id: string) => `/gaps/${id}/actions`,
		// Testing rail — list/start share the same collection route; GET lists newest first, POST starts one.
		TEST_RUNS: (id: string) => `/gaps/${id}/test-runs`,
		TEST_RUN_BY_ID: (id: string, runSetId: string) => `/gaps/${id}/test-runs/${runSetId}`,
		CANCEL_TEST_RUN: (id: string, runSetId: string) => `/gaps/${id}/test-runs/${runSetId}/cancel`
	},

	/** Playground: test chats with Kim kept in Kim AI itself. */
	PLAYGROUND: {
		CHATS: '/playground/chats',
		CHAT: (chatId: string) => `/playground/chats/${chatId}`,
		MESSAGES: (chatId: string) => `/playground/chats/${chatId}/messages`,
		TURN: (chatId: string, turnId: string) => `/playground/chats/${chatId}/turns/${turnId}`
	},

	/** Sales Agent settings (the Settings tab), brand scoped by `?bu_id`. */
	SALES_AGENT: {
		PERFORMANCE: "/sales-agent/performance",
		SETTINGS: '/sales-agent/settings'
	}
} as const;
